import { useMemo } from 'react';
import type {
  DataTableCellIdCoordinate,
  DataTableCellIndexCoordinate,
  DataTableCellReference,
} from '@/components/UI/DataTableV2/DataTable.model';
import { toCellRef } from '../DataTable.util';
import { useDataTable } from '../DataTableProvider';

export interface UseCellCoordinateReturn {
  idCoordinate: DataTableCellIdCoordinate | undefined;
  cellRef: DataTableCellReference;
}

/**
 * Transform index coordinates to ID coordinates (memoized)
 *
 * This hook converts cell index coordinates (row/column numbers) to ID coordinates
 * (API IDs) and generates the cell reference string used for event subscriptions.
 *
 * @param coordinate - Cell index coordinate (column and row numbers)
 * @returns Object containing the ID coordinate and cell reference string
 */
export const useCellCoordinate = (coordinate: DataTableCellIndexCoordinate): UseCellCoordinateReturn => {
  const { dataTableService } = useDataTable();

  const idCoordinate = useMemo(
    () => dataTableService.fromIndexCoordinate(coordinate),
    [coordinate.column, coordinate.row, dataTableService]
  );

  const cellRef = useMemo(() => toCellRef(idCoordinate), [idCoordinate]);

  return { idCoordinate, cellRef };
};
